import React from "react";
import { DragOverlay, useDndContext, useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { usePageBuilder } from "../../context/PageBuilderContext";
import { ComponentRenderer } from "./ComponentRenderer";
import { IComponentNode } from "../../types/pageBuilder.types";
import { componentTemplates } from "./ComponentPalette";
import { FaPlus } from "react-icons/fa";

interface CanvasProps {
 isPreviewMode?: boolean;
}

const CanvasContent: React.FC<CanvasProps> = ({ isPreviewMode = false }) => {
 const { layout } = usePageBuilder();

 if (!layout) {
  return (
   <div className="flex items-center justify-center h-full">
    <div className="text-center">
     <p className="text-gray-500 mb-4">No layout defined</p>
     <p className="text-sm text-gray-400">Add components from the palette to get started</p>
    </div>
   </div>
  );
 }

 const handleComponentClick = (e: React.MouseEvent, _component: IComponentNode) => {
  // Don't select component on click - only select via edit icon
  e.stopPropagation();
 };

 // Get all sortable IDs
 const getSortableIds = (node: IComponentNode): string[] => {
  const ids: string[] = [];
  if (node.children) {
   node.children.forEach((child) => {
    ids.push(child.id);
    ids.push(...getSortableIds(child));
   });
  }
  return ids;
 };

 const sortableIds = getSortableIds(layout);

 return (
  <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
   <div className="w-full min-h-full p-8">
    <div className="max-w-7xl mx-auto bg-white min-h-screen">
     <ComponentRenderer
      component={layout}
      isPreviewMode={isPreviewMode}
      onComponentClick={handleComponentClick}
      depth={0}
     />
    </div>
   </div>
  </SortableContext>
 );
};

const RootDroppable: React.FC<{ children: React.ReactNode; isPreviewMode: boolean }> = ({ children, isPreviewMode }) => {
 const { setNodeRef, isOver } = useDroppable({
  id: "root-canvas",
  disabled: isPreviewMode,
 });
 const { active } = useDndContext();
 const { selectedDropTarget } = usePageBuilder();
 const isDraggingSomething = !!active;
 const isSelectedAsDropTarget = selectedDropTarget === "root-canvas";

 return (
  <div
   ref={setNodeRef}
   className={`w-full min-h-full transition-all duration-200 ${
    isSelectedAsDropTarget
     ? "ring-2 ring-green-500 bg-green-100/50"
     : isOver 
     ? "ring-2 ring-blue-500 bg-blue-100/50" 
     : isDraggingSomething
     ? "bg-gray-50/30"
     : ""
   }`}
  >
   {children}
  </div>
 );
};

export const Canvas: React.FC<CanvasProps> = ({ isPreviewMode = false }) => {
 const { layout, selectedDropTarget, setSelectedDropTarget } = usePageBuilder();
 const { active } = useDndContext();

 const handleRootPlusClick = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setSelectedDropTarget("root-canvas");
 };

 if (!layout) {
  const isRootSelected = selectedDropTarget === "root-canvas";
  return (
   <RootDroppable isPreviewMode={isPreviewMode}>
    <div className="flex items-center justify-center h-full">
     <div className="text-center relative">
      {!isPreviewMode && (
       <button
        type="button"
        onClick={handleRootPlusClick}
        className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white p-3 hover:bg-blue-600 transition-colors z-10 ${
         isRootSelected ? "ring-2 ring-green-500 ring-offset-2" : ""
        }`}
        title="Click to select as drop target, then click a component from palette"
       >
        <FaPlus className="text-xl" />
       </button>
      )}
      <p className="text-gray-500 mb-4">No layout defined</p>
      <p className="text-sm text-gray-400">Add components from the palette to get started</p>
      <p className={`text-xs mt-2 ${isRootSelected ? "text-green-700 font-medium" : "text-gray-400"}`}>
       {isRootSelected ? "Selected - Click a component from palette" : "Drag components here or click + to select drop target"}
      </p>
     </div>
    </div>
   </RootDroppable>
  );
 }

 return (
  <>
   <RootDroppable isPreviewMode={isPreviewMode}>
    <CanvasContent isPreviewMode={isPreviewMode} />
   </RootDroppable>
   <DragOverlay>
    {active?.id.toString().startsWith("palette-") ? (
     <div className="px-3 py-2 border border-gray-200 bg-white ">
      {componentTemplates.find((t) => `palette-${t.type}` === active.id.toString())?.label}
     </div>
    ) : null}
   </DragOverlay>
  </>
 );
};

